import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ActiveConversion,
  ActiveConversionsPaged,
  FailedUpload,
  PaginationQueryActiveConversions,
  QueueStatus,
  VideoQueueService,
} from '../../_services/video-queue.service';

import { BsModalService } from 'ngx-bootstrap/modal';
import { ActionHeaderRendererComponent } from '../../pipe-inspection/components/ag-grid-renderers/action-header-renderer/action-header-renderer.component';
import { ResetColumnsModalComponent } from '../../shared/components/reset-columns-modal/reset-columns-modal.component';
import { FileSizePipeStandalone } from '../../upload/_pipes/file-size.pipe';

// Initialize ag-grid license
LicenseManager.setLicenseKey(environment.agGridLicense);

@Component({
  selector: 'app-video-queue-dashboard',
  templateUrl: './video-queue-dashboard.component.html',
  styleUrls: ['./video-queue-dashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VideoQueueDashboardComponent implements OnInit, OnDestroy {
  queueStatus: QueueStatus | null = null;
  activeConversions: ActiveConversionsPaged | null = null;
  failedUploads: FailedUpload[] = [];
  loading = true;
  error: string | null = null;
  lastUpdated: Date = new Date();
  retrying: { [key: string]: boolean } = {};

  // Pagination properties (ngx-pagination pattern)
  currentPage = 1;
  pageSize = 50;
  allItems = 0;
  paginationArray: any[] = [];

  // Sorting properties
  sortField = 'startedat';
  sortDirection = 'asc';

  // ag-Grid properties
  private gridApi!: GridApi;
  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    comparator: () => 0,
    resizable: true,
    suppressHeaderMenuButton: true,
  };
  rowHeight = 48;
  suppressPaginationPanel = true;

  // Column visibility management
  columnVisibility: { [key: string]: boolean } = {
    name: true,
    statusOFProcessedFiles: true,
    tenant: true,
    files: true,
    started: true,
    duration: true,
    actions: true,
  };

  // Columns that cannot be hidden (always visible)
  lockedColumns: { [key: string]: boolean } = {
    name: true,
    actions: true,
  };

  showColumnSelector = false;
  columnSearchText = '';

  // Make Object.keys available in template
  Object = Object;

  gridContext = {
    toggleColumnSelector: () => this.toggleColumnSelector(),
    resetToDefaultLayout: () => this.openResetColumnsModal(),
  };

  private refreshSubscription?: Subscription;
  private activeConversionsSubscription?: Subscription;

  private fileSizePipe = new FileSizePipeStandalone();

  // ProcessedFileEnum mapping
  private statusLabels: { [key: number]: string } = {
    0: 'None',
    1: 'Open',
    2: 'Success',
    3: 'In Progress',
    4: 'Failed',
    5: 'In Queue',
  };

  constructor(
    private videoQueueService: VideoQueueService,
    private router: Router,
    private modalService: BsModalService
  ) {
    this.initializeColumnDefs();
  }

  initializeColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: 'Upload Name',
        field: 'name',
        colId: 'name',
        flex: 2,
        minWidth: 135,
        cellStyle: { 'overflow-wrap': 'anywhere' },
        valueGetter: (params: any) => params.data?.name || '',
      },
      {
        headerName: 'Status',
        field: 'statusOFProcessedFiles',
        colId: 'statusOFProcessedFiles',
        flex: 1,
        minWidth: 100,
        valueGetter: (params: any) => {
          const status = params.data?.statusOFProcessedFiles;
          return status !== null && status !== undefined ? this.statusLabels[status] || status : '';
        },
        cellRenderer: (params: any) => {
          const status = params.data?.statusOFProcessedFiles;
          const label = this.statusLabels[status] || status || '';
          let badgeClass = 'status-badge';

          switch (status) {
            case 2: // Success
              badgeClass += ' status-success';
              break;
            case 3: // InProgress
              badgeClass += ' status-primary';
              break;
            case 4: // Failed
              badgeClass += ' status-danger';
              break;
            case 5: // InQueue
              badgeClass += ' status-warning';
              break;
            case 1: // Open
              badgeClass += ' status-info';
              break;
            default: // None
              badgeClass += ' status-secondary';
              break;
          }

          return `<span class="${badgeClass}">${label}</span>`;
        },
      },
      {
        headerName: 'Tenant',
        field: 'tenant',
        colId: 'tenant',
        flex: 1,
        minWidth: 90,
        valueGetter: (params: ValueGetterParams<ActiveConversion>) =>
          params.data?.tenantDisplayName || params.data?.tenant || '',
      },
      {
        headerName: 'Files',
        field: 'fileCount',
        colId: 'files',
        minWidth: 150,
        width: 150,
        valueGetter: (params: any) => {
          const count = params.data?.fileCount || 0;
          const files = params.data?.files || [];
          const totalSize = files.reduce((sum: number, file: any) => sum + (file.sizeOfFile || 0), 0);
          const formattedSize = this.fileSizePipe.transform(totalSize);
          return `${count} files (${formattedSize})`;
        },
      },
      {
        headerName: 'Started',
        field: 'startedAt',
        colId: 'started',
        flex: 1,
        minWidth: 90,
        valueFormatter: (params) => {
          if (!params.value) return 'N/A';
          const date = new Date(params.value);
          return date.toLocaleString('de-DE', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      },
      {
        headerName: 'Duration',
        field: 'duration',
        colId: 'duration',
        minWidth: 100,
        width: 120,
        valueFormatter: (params) => this.formatDuration(params.value),
      },
      {
        headerName: '',
        field: 'actions',
        headerComponent: ActionHeaderRendererComponent,
        valueGetter: () => '', // Empty cell content
        minWidth: 70,
        maxWidth: 70,
        resizable: false,
        sortable: false,
        filter: false,
        suppressMovable: true, // Cannot be moved
        suppressHeaderMenuButton: true, // Remove header menu button
        suppressHeaderFilterButton: true, // Remove header filter button
      },
    ];
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  ngOnInit(): void {
    this.loadData();

    // Auto-refresh queue status every 1 hour
    this.refreshSubscription = interval(3600000)
      .pipe(switchMap(() => this.videoQueueService.getQueueStatus()))
      .subscribe({
        next: (status) => {
          this.queueStatus = status;
          this.lastUpdated = new Date();
        },
        error: (err) => {
          console.error('Auto-refresh failed:', err);
        },
      });

    // Auto-refresh active conversions every 10 seconds
    this.activeConversionsSubscription = interval(10000)
      .pipe(switchMap(() => this.videoQueueService.getActiveConversions(this.buildPaginationQuery())))
      .subscribe({
        next: (conversions) => {
          this.activeConversions = conversions;
          this.rowData = conversions.conversions;
          this.updatePaginationInfo(conversions);
        },
        error: (err) => {
          console.error('Active conversions refresh failed:', err);
        },
      });
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
    this.activeConversionsSubscription?.unsubscribe();
  }

  loadData(): void {
    this.loading = true;
    this.error = null;

    this.videoQueueService.getQueueStatus().subscribe({
      next: (status) => {
        this.queueStatus = status;
        this.loadActiveConversions();
        this.loadFailedUploads();
      },
      error: (err) => {
        console.error('Queue status error:', err);
        let errorMessage = 'Failed to load queue status: ';

        if (err.status === 0) {
          errorMessage += 'Cannot connect to backend. Please check if the backend is running.';
        } else if (err.status === 401) {
          errorMessage += 'Unauthorized. Please log in as an admin.';
        } else if (err.status === 404) {
          errorMessage += 'API endpoint not found. Please check backend configuration.';
        } else if (err.error && err.error.message) {
          errorMessage += err.error.message;
        } else if (err.message) {
          errorMessage += err.message;
        } else if (err.statusText) {
          errorMessage += err.statusText;
        } else {
          errorMessage += `HTTP ${err.status} error`;
        }

        this.error = errorMessage;
        this.loading = false;
      },
    });
  }

  loadActiveConversions(): void {
    this.videoQueueService.getActiveConversions(this.buildPaginationQuery()).subscribe({
      next: (conversions) => {
        this.activeConversions = conversions;
        this.rowData = conversions.conversions;
        this.updatePaginationInfo(conversions);
        this.lastUpdated = new Date();
        this.loading = false;
      },
      error: (err) => {
        console.error('Active conversions error:', err);
        let errorMessage = 'Failed to load active conversions: ';

        if (err.status === 0) {
          errorMessage += 'Cannot connect to backend.';
        } else if (err.error && err.error.message) {
          errorMessage += err.error.message;
        } else if (err.message) {
          errorMessage += err.message;
        } else if (err.statusText) {
          errorMessage += err.statusText;
        } else {
          errorMessage += `HTTP ${err.status} error`;
        }

        this.error = errorMessage;
        this.loading = false;
      },
    });
  }

  loadFailedUploads(): void {
    this.videoQueueService.getFailedUploads().subscribe({
      next: (failed) => {
        this.failedUploads = failed;
      },
      error: (err) => {
        console.error('Failed uploads error:', err);
      },
    });
  }

  buildPaginationQuery(): PaginationQueryActiveConversions {
    return {
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      orderBy: `${this.sortField} ${this.sortDirection}`,
    };
  }

  updatePaginationInfo(response: ActiveConversionsPaged): void {
    this.allItems = response.totalCount;
    this.paginationArray = new Array(response.totalCount).fill(null);
  }

  renderPage(event: number): void {
    this.currentPage = event;
    this.loadActiveConversions();
  }

  onSortChanged(): void {
    if (!this.gridApi) return;

    const sortedColumns = this.gridApi.getColumnState().filter((col) => col.sort);

    if (sortedColumns && sortedColumns.length > 0) {
      const sort = sortedColumns[0];
      const field = sort.colId;
      const direction = sort.sort || 'asc';

      // Map AG Grid column IDs to backend sort fields
      switch (field) {
        case 'name':
          this.sortField = 'name';
          break;
        case 'statusOFProcessedFiles':
          this.sortField = 'status';
          break;
        case 'tenant':
          this.sortField = 'tenant';
          break;
        case 'files':
          this.sortField = 'filecount';
          break;
        case 'started':
          this.sortField = 'startedat';
          break;
        case 'duration':
          this.sortField = 'startedat'; // Duration is computed from startedat
          break;
        default:
          this.sortField = 'startedat';
          break;
      }

      this.sortDirection = direction;
    } else {
      // Default sorting when no sort is applied
      this.sortField = 'startedat';
      this.sortDirection = 'asc';
    }

    // Fetch data with new sort
    this.currentPage = 1;
    this.loadActiveConversions();
  }

  refresh(): void {
    this.loadData();
  }

  retryFailedUpload(uploadId: string): void {
    if (!confirm('Are you sure you want to retry this failed upload? It will be moved back to the queue.')) {
      return;
    }

    this.retrying[uploadId] = true;
    this.videoQueueService.retryFailedUpload(uploadId).subscribe({
      next: (response) => {
        alert(response.message);
        this.loadData();
        this.retrying[uploadId] = false;
      },
      error: (err) => {
        this.error = `Failed to retry upload: ${err.message || err.statusText || 'Unknown error'}`;
        this.retrying[uploadId] = false;
      },
    });
  }

  formatDuration(duration: string | null): string {
    if (!duration) return 'N/A';

    // Parse duration string (format: HH:MM:SS or HH:MM:SS.mmmmmmm)
    const parts = duration.split(':');
    if (parts.length !== 3) return duration;

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2].split('.')[0], 10);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  goBack(): void {
    this.router.navigate(['admin/upload-content']);
  }

  toggleColumnSelector(): void {
    this.showColumnSelector = !this.showColumnSelector;
  }

  closeColumnSelector(): void {
    this.showColumnSelector = false;
  }

  toggleColumnVisibility(columnField: string): void {
    // Prevent hiding locked columns
    if (this.lockedColumns[columnField] && this.columnVisibility[columnField]) {
      return; // Don't allow hiding locked columns
    }

    this.columnVisibility[columnField] = !this.columnVisibility[columnField];

    // Update the grid column visibility
    if (this.gridApi) {
      this.gridApi.setColumnsVisible([columnField], this.columnVisibility[columnField]);
    }
  }

  getColumnDisplayName(columnField: string): string {
    const column = this.columnDefs.find((col) => col.field === columnField);
    return column?.headerName || columnField;
  }

  getFilteredColumns(): string[] {
    const allColumns = Object.keys(this.columnVisibility);
    if (!this.columnSearchText) {
      return allColumns.filter((columnField) => {
        return !this.isColumnLocked(columnField);
      });
    }
    return allColumns.filter((columnField) => {
      const displayName = this.getColumnDisplayName(columnField).toLowerCase();
      return displayName.includes(this.columnSearchText.toLowerCase()) && !this.isColumnLocked(columnField);
    });
  }

  isColumnLocked(columnField: string): boolean {
    return !!this.lockedColumns[columnField];
  }

  onColumnSearchChange(): void {
    // Search is handled by getFilteredColumns()
  }

  updateColumnDefinitions(): void {
    this.columnDefs = this.columnDefs.map((colDef) => {
      // Don't update the actions column
      if (colDef.field === 'actions') {
        return { ...colDef, hide: false }; // Ensure it's always visible
      }
      return {
        ...colDef,
        hide:
          colDef.field && Object.prototype.hasOwnProperty.call(this.columnVisibility, colDef.field)
            ? !this.columnVisibility[colDef.field]
            : false,
      };
    });

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
    }
  }

  openResetColumnsModal(): void {
    const modalRef = this.modalService.show(ResetColumnsModalComponent, {
      class: 'modal-dialog-centered',
      backdrop: 'static',
    });

    if (modalRef.content) {
      modalRef.content.confirmResetEvent.subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.resetToDefaultLayout();
        }
      });
    }
  }

  resetToDefaultLayout(): void {
    // Reset visibility to default (all visible)
    this.columnVisibility = {
      name: true,
      statusOFProcessedFiles: true,
      tenant: true,
      files: true,
      started: true,
      duration: true,
      actions: true,
    };

    // Update column definitions
    this.updateColumnDefinitions();

    // Reset grid to default state
    if (this.gridApi) {
      // Reset column state to default
      this.gridApi.resetColumnState();

      // Apply column visibility
      Object.keys(this.columnVisibility).forEach((columnField) => {
        this.gridApi.setColumnsVisible([columnField], this.columnVisibility[columnField]);
      });
    }
  }

  onCellClicked(event: any): void {
    this.router.navigate(['./admin/upload-content/detail', event.data.id]);
  }
}
